import { Fragment, h } from "preact";
import { useSelector } from "react-redux";
import { useEffect, useRef, useState } from "preact/hooks";
import { useNavigate } from "react-router-dom";

import { selectIsLoggedIn } from "../store";
import {
  classNames,
  renderHtml,
  replaceRedactions,
  showHostOfUrl,
} from "../helpers";
import i18n from "../i18n";
import appSticky from "../appSticky";

import Breadcrumbs from "./Breadcrumbs.jsx";
import Carousel from "./Carousel.jsx";
import Icon from "./Icon.jsx";
import ImageCarouselDialog from "./partnerProgramPage/ImageCarouselDialog.jsx";
import ListItem from "./ListItem.jsx";
import Tag from "./Tag.jsx";
import CircularButton from "./CircularButton.jsx";

export default function PartnerDetails({ partner }) {
  const navigate = useNavigate();
  const benefitsBox = useRef(null);
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const [carouselDialogOpen, setCarouselDialogOpen] = useState(false);

  useEffect(() => {
    appSticky(benefitsBox.current, {
      offsetTop: 196,
    });
  }, []);

  return (
    <Fragment>
      <div class={classNames("partner-page partner-details")}>
        <CircularButton
          circular
          class="ml-8 hide-on-med-and-down"
          trackingName="partnerGoBack"
          onClick={() => navigate("/marketplace")}
        >
          chevron_left
        </CircularButton>

        <div
          class={`partner-page__header partner-details__header ${
            partner.headerImage.style == "cover" &&
            "partner-page__header--cover"
          }`}
          style={{
            backgroundColor: partner.headerImage.backgroundColor,
            backgroundImage: `url(${partner.headerImage.url})`,
            backgroundPosition: partner.headerImage.position,
          }}
        >
          <Breadcrumbs
            class="hide-on-large mt-24 ml-8"
            items={[
              {
                to: "/marketplace",
                title: i18n.t("pages.partner_page.back_to_marketplace"),
              },
              partner.title,
            ]}
          />
          <div class="row partner-details__mobile-info">
            <div class="col s12">
              <div class="partner-page__breadcrumb mt-24">
                <div class="mt-16">
                  {partner.tags.map((tag) => (
                    <Tag grey>{tag}</Tag>
                  ))}
                </div>

                <h1 class="t-h4 t-h4--margin">{partner.title}</h1>

                <div class="t-body2 color-shades-charcoal mb-32">
                  {renderHtml(partner.body)}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="partner-details__content row">
          <div
            class="col s12 l4 partner-page__benefits-box"
            ref={benefitsBox}
            data-sticky-wrap="true"
          >
            <div
              class={classNames(
                "partner-page__logo-image",
                partner.logoImage.square && "partner-page__logo-image--square"
              )}
              style={{ backgroundColor: partner.logoImage.backgroundColor }}
            >
              <img
                src={partner.logoImage.url}
                alt={`Logo of ${partner.title}`}
              />
            </div>

            <div class="partner-page__logo-card">
              <h5 class="t-overline t-h6--margin mt-32">
                {i18n.t("pages.partner_page.benefits")}
              </h5>

              <div class="markdown-body partner-page__benefits">
                {renderHtml(
                  replaceRedactions(
                    partner.benefits || partner.benefitsLoggedOut,
                    false
                  )
                )}
              </div>

              <div class="partner-page__logo-card__button">
                {isLoggedIn ? (
                  <a
                    href={partner.brandedLandingPage}
                    class="button button--default"
                    data-tk-click="button.click"
                    data-tk-params={JSON.stringify({
                      name: "Claim benefits",
                      type: "partner page cta",
                    })}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    {i18n.t("pages.partner_page.claim_benefits")}
                  </a>
                ) : (
                  <a
                    href={`/login?redirect_to=/app/marketplace/${encodeURIComponent(
                      partner.id
                    )}`}
                    class="button button--default"
                    data-tk-click="button.click"
                    data-tk-params={JSON.stringify({
                      name: "Log in to claim benefits",
                      type: "partner page cta",
                    })}
                  >
                    {i18n.t("pages.partner_page.login_for_benefits")}
                  </a>
                )}
              </div>
            </div>
            <h4 class="t-subtitle1 t-subtitle1--margin mt-48">
              {i18n.t("pages.partner_page.contact")}
            </h4>
            {partner.website && (
              <ListItem overline={i18n.t("pages.partner_page.website")}>
                <a class="link--invisible" href={partner.website}>
                  {showHostOfUrl(partner.website)}
                </a>
              </ListItem>
            )}
            {partner.email && (
              <ListItem overline={i18n.t("pages.partner_page.email")}>
                <a class="link--invisible" href={`mailto:${partner.email}`}>
                  {partner.email}
                </a>
              </ListItem>
            )}
            {partner.phone && (
              <ListItem overline={i18n.t("pages.partner_page.phone")}>
                <a class="link--invisible" href={`tel:${partner.phone}`}>
                  {partner.phone}
                </a>
              </ListItem>
            )}
          </div>

          <div class="col s12 l6 push-l1 mt-48">
            <div class="hide-on-med-and-down">
              {partner.tags.map((tag) => (
                <Tag noHover grey>
                  {tag}
                </Tag>
              ))}

              <h1 class="t-h2 t-h2--margin">{partner.title}</h1>
              <div class="markdown-body partner-page__body mb-48">
                {renderHtml(partner.body)}
              </div>
            </div>
            <h2 class="t-h6 t-h6--margin">About</h2>
            <div class="markdown-body partner-page__about mb-32">
              {renderHtml(partner.about)}
            </div>
            <h2 class="t-h6 t-h6--margin">Key Features</h2>
            <div class="markdown-body partner-page__features mb-32">
              {renderHtml(partner.keyFeatures)}
            </div>
            <ListItem
              big
              iconFront={<Icon class="color-brand-jade">language</Icon>}
              secondLine={partner.supportedLanguages}
            >
              {i18n.t("pages.partner_page.supportedLanguages")}
            </ListItem>
            <ListItem
              big
              iconFront={<Icon class="color-brand-jade">flag</Icon>}
              secondLine={partner.supportedRegions}
            >
              {i18n.t("pages.partner_page.supportedRegions")}
            </ListItem>
            <h2 class="t-h6 t-h6--margin mt-32">
              {i18n.t("pages.partner_page.pricing")}
            </h2>
            <div class="markdown-body partner-page__pricing mb-32">
              {renderHtml(partner.pricing)}
            </div>
            <h2 class="t-h6 t-h6--margin">
              {i18n.t("pages.partner_page.productImages")}
            </h2>
            <Carousel
              buttonsOnHover
              images={partner.productImages}
              onSlideClick={(item) => setCarouselDialogOpen(item)}
            />
          </div>
        </div>
      </div>
      {carouselDialogOpen && (
        <ImageCarouselDialog
          images={partner.productImages}
          initImage={partner.productImages.indexOf(carouselDialogOpen)}
          onClose={() => setCarouselDialogOpen(false)}
        />
      )}
    </Fragment>
  );
}
